import { mapToCanActivate, mapToResolve, Routes } from '@angular/router';
import { coreRoutes } from '@next/core-lib/app';
import { AtLeastAdministratorRoleGuard } from '@next/core-lib/authorization';
import { LocationGuard } from '@next/core-lib/relationships';
import { Observable } from 'rxjs';

// Flock
import { DeliveryRouterComponent } from './delivery/components/delivery-router/delivery-router.component';
import { DeliveryRoutes } from './delivery/delivery.routes';
import { FeedAdviceRouterComponent } from './feed-advice/components/feed-advice-router/feed-advice-router.component';
import { FeedAdviceRoutes } from './feed-advice/feed-advice.routes';
import { AnimalCountReportComponent } from './flock/components/animal-count-report/animal-count-report.component';
import { BreederFlockOverviewComponent } from './flock/components/breeders/flock-overview/breeder-flock-overview.component';
import { CarbonFootprintComponent } from './flock/components/emission/carbon-footprint/carbon-footprint.component';
import { FarmResourcesComponent } from './flock/components/emission/farm-resources/farm-resources.component';
import { FeedBalanceComponent } from './flock/components/feed-balance/feed-balance.component';
import { FlockReportComponent } from './flock/components/flock-report/flock-report.component';
import { LayerFlockOverviewComponent } from './flock/components/layers/flock-overview/layer-flock-overview.component';
import { FlockComponent } from './flock/flock.component';
import { BreedersGuard } from './flock/guards/breeders.guard';
import { FlocksGuard } from './flock/guards/flocks.guard';
import { AnimalCountCardsResolver } from './flock/resolvers/animalcount.resolver';

// Product
import { ProductDetailComponent } from './product/components/product-detail/product-detail.component';
import { ProductOverviewComponent } from './product/components/product-overview/product-overview.component';
import { ProductComponent } from './product/product.component';
import { ProductOverviewResolver } from './product/resolvers/product-overview.resolver';

// Settings
import { ReferenceSchemeRouterComponent } from './reference-scheme/components/reference-scheme-router/reference-scheme-router.component';
import { ReferenceSchemeRoutes } from './reference-scheme/reference-scheme.routes';
import { BarnOverviewComponent } from './settings/components/barn-overview/barn-overview.component';
import { SettingsOverviewComponent } from './settings/components/settings-overview/settings-overview.component';
import { SustainabilityOverviewComponent } from './settings/components/sustainability-overview/sustainability-overview.component';
import { BarnResolver } from './settings/resolvers/barn.resolver';
import { FarmSettingsResolver } from './settings/resolvers/farm-settings.resolver';

// Delivery

// Reference Scheme

// Feed Advice
import { UnauthorizedComponent } from './shared/components/unauthorized/unauthorized.component';

export const appRoutes: Routes = [
  // Flock routes
  { path: '', redirectTo: 'flock', pathMatch: 'full' },
  {
    path: 'flock',
    component: FlockComponent,
    children: [
      {
        path: '',
        component: LayerFlockOverviewComponent,
        data: { name: 'flock-overview' },
      },
      {
        path: 'feed-balance/:id',
        component: FeedBalanceComponent,
        canActivate: mapToCanActivate([FlocksGuard]),
        canDeactivate: [(component: FeedBalanceComponent) => component.canLeave()],
      },
      {
        path: 'flock-report/:id',
        component: FlockReportComponent,
      },
      {
        path: 'animalcount-report/:id',
        component: AnimalCountReportComponent,
        resolve: { animalCountCards: AnimalCountCardsResolver },
      },
    ],
    canActivate: mapToCanActivate([LocationGuard]),
  },
  {
    path: 'breeders',
    component: FlockComponent,
    children: [
      {
        path: '',
        component: BreederFlockOverviewComponent,
        data: { name: 'breeder-overview' },
      },
    ],
    canActivate: mapToCanActivate([BreedersGuard, LocationGuard]),
  },
  {
    path: 'farm-resources',
    component: FarmResourcesComponent,
    canActivate: mapToCanActivate([LocationGuard, FlocksGuard]),
  },
  {
    path: 'carbon-footprint',
    component: CarbonFootprintComponent,
    canActivate: mapToCanActivate([LocationGuard, FlocksGuard]),
  },

  // Product routes
  {
    path: 'product',
    component: ProductComponent,
    children: [
      {
        path: '',
        component: ProductOverviewComponent,
        canActivate: mapToCanActivate([LocationGuard, AtLeastAdministratorRoleGuard]),
        data: { name: 'product-overview' },
        resolve: {
          data: mapToResolve<Observable<boolean>>(ProductOverviewResolver),
        },
      },
      {
        path: 'create',
        component: ProductDetailComponent,
        canActivate: mapToCanActivate([LocationGuard, AtLeastAdministratorRoleGuard]),
        data: { name: 'product-detail' },
      },
      {
        path: 'edit/:id',
        component: ProductDetailComponent,
        canActivate: mapToCanActivate([LocationGuard, AtLeastAdministratorRoleGuard]),
        data: { name: 'product-detail' },
      },
    ],
    canActivate: mapToCanActivate([LocationGuard, AtLeastAdministratorRoleGuard]),
  },

  // Settings routes
  { path: 'settings', redirectTo: 'settings/barn', pathMatch: 'full' },
  {
    path: 'settings',
    component: SettingsOverviewComponent,
    children: [
      {
        path: 'barn',
        component: BarnOverviewComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        resolve: {
          isLoaded: mapToResolve<Observable<boolean>>(BarnResolver),
        },
        data: { name: 'barn-overview' },
      },
      {
        path: 'sustainability',
        component: SustainabilityOverviewComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        resolve: {
          isLoaded: mapToResolve<Observable<boolean>>(FarmSettingsResolver),
        },
        data: { name: 'sustainability-overview' },
      },
    ],
    canActivate: mapToCanActivate([LocationGuard]),
  },

  // Delivery routes
  {
    path: 'delivery',
    component: DeliveryRouterComponent,
    children: DeliveryRoutes(),
  },

  // Reference Scheme routes
  {
    path: 'reference-scheme',
    component: ReferenceSchemeRouterComponent,
    children: ReferenceSchemeRoutes,
  },

  // Feed Advice routes
  {
    path: 'feed-advice',
    component: FeedAdviceRouterComponent,
    children: FeedAdviceRoutes(),
  },

  // Shared
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  ...coreRoutes,
];
